import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  CircularProgress,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useNetSuiteProductionRfq } from '../../../config/netsuite';

const PreflightRfqModal = ({ open, onClose, onConfirm, projects }) => {
  const [project, setProject] = useState(null);
  const [subsidiaries, setSubsidiaries] = useState([]);
  const [subsidiary, setSubsidiary] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [subsidiariesLoading, setSubsidiariesLoading] = useState(false);
  const [subsidiaryDropdownOpen, setSubsidiaryDropdownOpen] = useState(false);

  const handleProjectChange = async (_, selectedProject) => {
    setProject(selectedProject);
    setSubsidiary('');
    setSubsidiaries([]);
    setSubsidiaryDropdownOpen(false);

    if (!selectedProject) return;

    setSubsidiariesLoading(true);
    try {
      const res = await axios.get(
        `/api/NetSuite/CustomRecordPath?customPath=classification/${selectedProject.netSuiteId}/subsidiary&useProduction=${useNetSuiteProductionRfq}`
      );
      const newSubsidiaries = res.data?.items || [];

      if (newSubsidiaries.length > 0) {
        setSubsidiaries(newSubsidiaries);
        setTimeout(() => setSubsidiaryDropdownOpen(true), 100);
      } else {
        await runSubsidiaryFallback(selectedProject);
      }
    } catch (err) {
      await runSubsidiaryFallback(selectedProject);
    } finally {
      setSubsidiariesLoading(false);
    }
  };

  const runSubsidiaryFallback = async (project) => {
    try {
      const backupRes = await axios.get(
        `/api/NetSuite/CustomRecordPath?customPath=classification/${project.netSuiteId}&useProduction=${useNetSuiteProductionRfq}`
      );
      const backupSubsidiary = [backupRes.data?.custrecord138];
      setSubsidiaries(backupSubsidiary);
      if (backupSubsidiary.length > 0) {
        setTimeout(() => setSubsidiaryDropdownOpen(true), 100);
      }
    } catch {
      toast.error('Failed to fetch subsidiaries from fallback.');
    }
  };

  const generateNewRfqName = async (afeId, afeNumber, rfqName) => {
    try {
      const existingRfqsResponse = await axios.get(`/api/RfqHeader/rfq?projectId=${afeId}&includeDeleted=true`);
      const existingRfqs = existingRfqsResponse.data;

      const baseName = `${afeNumber}-PRO-RFQT-`;
      let newRfqNumber = 100;

      if (existingRfqs?.length) {
        const lastRfq = existingRfqs.reduce((max, rfq) => {
          const match = rfq.name?.match(/-PRO-RFQT-(\d{4})/);
          const num = match ? parseInt(match[1], 10) : 0;
          return num > max ? num : max;
        }, 0);
        newRfqNumber = lastRfq + 1;
      }

      const formatted = newRfqNumber.toString().padStart(4, '0');
      return `${baseName}${formatted} - ${rfqName}`;
    } catch (error) {
      console.error('Error generating request name:', error);
      toast.error('Failed to generate request name.');
      return null;
    }
  };

  const handleConfirm = async () => {
    if (!project || !subsidiary || !name) return;

    setLoading(true);
    try {
      const rfqName = await generateNewRfqName(project.projectId, project.afeNumber, name || 'PO Request');
      if (!rfqName) {
        setLoading(false);
        return;
      }

      onConfirm({
        rfqName,
        baseProjectId: project.projectId,
        baseSubsidiaryId: subsidiary.id,
        pair: {
          projectId: project.projectId,
          budgetId: project.netSuiteId,
          subsidiaryId: subsidiary.id,
          subsidiaryName: subsidiary.refName,
        },
      });
      onClose();
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Start New Request For Quote</DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              label="Request Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              fullWidth
              placeholder="Example: Mainline Valves for Blackfin"
            />
          </Grid>
          <Grid item xs={12}>
            <Autocomplete
              options={projects}
              getOptionLabel={(option) => `${option.name} (AFE: ${option.afeNumber})`}
              value={project}
              onChange={handleProjectChange}
              isOptionEqualToValue={(option, value) => option.projectId === value.projectId}
              renderOption={(props, option) => (
                <li {...props} key={option.projectId}>
                  {option.name} (AFE: {option.afeNumber})
                </li>
              )}
              renderInput={(params) => (
                <TextField {...params} label="Base Project (For Mfiles Name Generation)" fullWidth />
              )}
            />
          </Grid>

          {project && (
            <Grid item xs={12}>
              <Autocomplete
                open={subsidiaryDropdownOpen}
                onOpen={() => setSubsidiaryDropdownOpen(true)}
                onClose={() => setSubsidiaryDropdownOpen(false)}
                options={subsidiaries}
                getOptionLabel={(option) => option.refName || ''}
                value={subsidiary || null}
                onChange={(_, selected) => setSubsidiary(selected)}
                loading={subsidiariesLoading}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Linked Subsidiary"
                    fullWidth
                    slotProps={{
                      input: {
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {subsidiariesLoading ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      },
                    }}
                  />
                )}
              />
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained" color="warning" disabled={loading}>
          Cancel
        </Button>
        <Button onClick={handleConfirm} disabled={!project || !subsidiary || !name || loading} variant="contained">
          {loading ? <CircularProgress size={20} /> : 'Continue'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PreflightRfqModal;
